import { Button } from '@chakra-ui/button';
import { Flex, Text } from '@chakra-ui/layout';
import { formatUnits, parseUnits } from '@ethersproject/units';
import CustomInput from 'components/shared/CustomInput';
import { BigNumber } from 'ethers';
import { useMemo } from 'react';
import { ERC20Token } from 'types';
import { truncate } from 'utils/address';
import formatNumber from 'utils/formatNumber';
import { sanitize } from 'utils/sanitize';

type Props = {
  tokenBalance: BigNumber;
  tokenDecimals: number;
  selectedAsset: ERC20Token;
  amount: string;
  setAmount: React.Dispatch<React.SetStateAction<string>>;
  assetSelector?: React.ReactElement | undefined;
  handleTransfer: () => void;
  submitButtonText: string;
  isDisabled?: boolean;
};

const TokenTransferForm: React.FC<Props> = ({
  tokenBalance,
  tokenDecimals,
  selectedAsset,
  amount,
  setAmount,
  assetSelector,
  handleTransfer,
  submitButtonText,
  isDisabled,
  children
}) => {
  const parsedAmount = useMemo(() => {
    if (
      amount &&
      amount.split('.').length === 2 &&
      amount.split('.')[1].length > tokenDecimals
    ) {
      return (
        amount.split('.')[0] +
        '.' +
        amount.split('.')[1].slice(0, tokenDecimals)
      );
    }
    return amount;
  }, [amount, tokenDecimals]);

  return (
    <Flex p={[4, 6]} borderRadius='xl' bg='gray.700' flexDir='column'>
      {assetSelector && (
        <>
          <Flex alignItems='center' justifyContent='space-between' mb={2}>
            <Text>Amount</Text>
            <Text>
              Balance:{' '}
              {tokenBalance
                ? formatNumber.token(tokenBalance, tokenDecimals)
                : '-'}{' '}
              {truncate(selectedAsset?.symbol)}
            </Text>
          </Flex>
          <CustomInput
            bg='gray.600'
            border='none'
            max={formatUnits(tokenBalance, tokenDecimals)}
            value={parsedAmount}
            tokenDecimals={tokenDecimals}
            onUserInput={(input) => setAmount(input)}
            showMaxButton
            inputRightElement={assetSelector}
            _placeholder={{
              color: 'gray.300'
            }}
          />
          {children}
          <Button
            mt={4}
            isFullWidth
            onClick={handleTransfer}
            disabled={
              parseUnits(sanitize(parsedAmount), tokenDecimals).lte(0) ||
              parseUnits(sanitize(parsedAmount), tokenDecimals).gt(
                selectedAsset.value.amount
              ) ||
              isDisabled
            }
          >
            {submitButtonText}
          </Button>
        </>
      )}
      {!assetSelector && <div>There are no tokens available for wrapping</div>}
    </Flex>
  );
};

export default TokenTransferForm;
